import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Download,
  AlertTriangle,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  Info,
  Monitor,
  LogOut,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { lgpdApi } from "../services/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

/**
 * /conta/privacidade — exposes LGPD Art. 18 rights (V/VI/IX) to the user.
 *
 * The backend already implements all the heavy lifting in /api/users/me/*
 * (see /app/backend/routes/users_lgpd.py — iter 113). This page is a thin
 * UI wrapper so users can self-serve without contacting support.
 *
 * Sections:
 *   1. Status do consentimento (versão, data, IP)
 *   2. Exportar meus dados (Art. 18 V)
 *   3. Revogar consentimento (Art. 8º §5º — suspends account)
 *   4. Excluir minha conta (Art. 18 VI — anonimization, irrevocable)
 */
const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function ContaPrivacidade() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [consent, setConsent] = useState(null);
  const [loadingConsent, setLoadingConsent] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Active sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [revokingSessionJti, setRevokingSessionJti] = useState(null);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // Delete-account dialog state
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    if (!user) {
      setLoadingConsent(false);
      setLoadingSessions(false);
      return;
    }
    (async () => {
      try {
        const data = await lgpdApi.getConsent();
        setConsent(data);
      } catch (e) {
        toast.error("Não foi possível carregar seu consentimento.");
      } finally {
        setLoadingConsent(false);
      }
    })();
    (async () => {
      try {
        const data = await lgpdApi.listSessions();
        setSessions(data?.sessions || []);
      } catch (e) {
        // Silent — endpoint pode falhar para contas antigas sem refresh token persistido.
      } finally {
        setLoadingSessions(false);
      }
    })();
  }, [user]);

  const refreshSessions = async () => {
    try {
      const data = await lgpdApi.listSessions();
      setSessions(data?.sessions || []);
    } catch (e) {
      // noop
    }
  };

  const handleRevokeSession = async (jti) => {
    setRevokingSessionJti(jti);
    try {
      await lgpdApi.revokeSession(jti);
      toast.success("Sessão encerrada.");
      await refreshSessions();
    } catch (e) {
      toast.error("Não foi possível encerrar esta sessão.");
    } finally {
      setRevokingSessionJti(null);
    }
  };

  const handleLogoutEverywhere = async () => {
    setLoggingOutAll(true);
    try {
      await lgpdApi.logoutEverywhere();
      toast.success("Todas as sessões foram encerradas. Você será desconectado.");
      setTimeout(() => {
        logout?.();
        navigate("/", { replace: true });
      }, 1200);
    } catch (e) {
      toast.error("Não foi possível encerrar todas as sessões.");
      setLoggingOutAll(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await lgpdApi.exportData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `empreende-meus-dados-${today}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download iniciado.");
    } catch (e) {
      toast.error("Não foi possível exportar seus dados.");
    } finally {
      setExporting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await lgpdApi.revokeConsent();
      toast.success("Consentimento revogado. Sua sessão será encerrada.");
      setTimeout(() => {
        logout?.();
        navigate("/", { replace: true });
      }, 1500);
    } catch (e) {
      toast.error("Não foi possível revogar o consentimento.");
      setRevoking(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
      toast.error("Digite o email exatamente como ele aparece na sua conta.");
      return;
    }
    setDeleting(true);
    try {
      await lgpdApi.deleteAccount({
        confirmEmail: deleteConfirmEmail.trim(),
        reason: deleteReason.trim() || undefined,
      });
      toast.success("Conta excluída. Você será desconectado.");
      setTimeout(() => {
        logout?.();
        navigate("/", { replace: true });
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível excluir a conta.");
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div
        className="container max-w-3xl mx-auto py-12 px-4 text-center"
        data-testid="conta-privacidade-page"
      >
        <Shield className="h-10 w-10 mx-auto text-emerald-600 mb-3" />
        <h1 className="text-2xl font-semibold tracking-tight mb-2">
          Minha Privacidade
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Para gerenciar seus dados pessoais (LGPD), faça login na sua conta.
        </p>
        <Button onClick={() => navigate("/perfil")} data-testid="login-redirect-btn">
          Fazer login
        </Button>
      </div>
    );
  }

  return (
    <div
      className="container max-w-3xl mx-auto py-8 px-4 space-y-6"
      data-testid="conta-privacidade-page"
    >
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-emerald-600" />
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            Minha Privacidade
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Aqui você exerce os direitos previstos na LGPD (Lei nº 13.709/2018):
          consultar como seus dados estão sendo tratados, exportá-los, revogar
          consentimento e excluir sua conta.
        </p>
      </header>

      {/* ===== Consent status ===== */}
      <Card className="p-6 space-y-3" data-testid="consent-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Status do consentimento</h2>
          {loadingConsent ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : consent?.is_active ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-medium"
              data-testid="consent-active-badge"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Ativo
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-2.5 py-0.5 text-xs font-medium"
              data-testid="consent-missing-badge"
            >
              <XCircle className="h-3.5 w-3.5" /> Não registrado
            </span>
          )}
        </div>

        {loadingConsent ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : consent?.is_active ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground">Dado em</dt>
              <dd className="font-medium" data-testid="consent-given-at">
                {formatDateTime(consent.given_at)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Versão dos Termos</dt>
              <dd className="font-medium">{consent.terms_version || "v1.0"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Versão da Política</dt>
              <dd className="font-medium">{consent.privacy_version || "v1.0"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">IP no momento do aceite</dt>
              <dd className="font-mono text-xs">{consent.ip_at_consent || "—"}</dd>
            </div>
          </dl>
        ) : (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-900 px-3 py-2 text-sm">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Você criou esta conta antes do registro estruturado de
              consentimento. Você ainda pode exercer todos os direitos LGPD
              abaixo. Para refrescar a impressão digital de consentimento, basta
              aceitar novamente os Termos ao re-cadastrar (não é obrigatório).
            </div>
          </div>
        )}
      </Card>

      {/* ===== Export data ===== */}
      <Card className="p-6 space-y-3" data-testid="export-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium">Baixar meus dados</h2>
            <p className="text-sm text-muted-foreground mt-1">
              LGPD Art. 18 V — Direito à portabilidade. Você recebe um arquivo
              JSON com todo o conteúdo associado ao seu perfil: negócios,
              avaliações, favoritos, reivindicações, cupons, inscrições em
              programas, notificações e histórico de auditoria.
            </p>
          </div>
        </div>
        <div className="pt-1">
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="secondary"
            data-testid="export-data-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparando…
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" /> Baixar JSON
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* ===== Active sessions ===== */}
      <Card className="p-6 space-y-3" data-testid="sessions-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium flex items-center gap-2">
              <Monitor className="h-5 w-5 text-emerald-600" />
              Sessões ativas
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Dispositivos em que sua conta está atualmente conectada. Você pode
              encerrar qualquer sessão individualmente ou todas de uma vez.
            </p>
          </div>
          {sessions.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogoutEverywhere}
              disabled={loggingOutAll}
              data-testid="logout-everywhere-btn"
            >
              {loggingOutAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <LogOut className="mr-2 h-4 w-4" />
                  Encerrar todas
                </>
              )}
            </Button>
          )}
        </div>

        {loadingSessions ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : sessions.length === 0 ? (
          <div className="rounded-lg bg-gray-50 px-3 py-3 text-sm text-muted-foreground">
            Nenhuma sessão persistente ativa. Você pode estar logado apenas no
            navegador atual via cookies de curta duração.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sessions.map((s) => (
              <li
                key={s.jti}
                className="flex items-center justify-between py-3 gap-4"
                data-testid={`session-row-${s.jti}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {s.device_label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Início: {formatDateTime(s.created_at)}
                    {s.last_used_at && (
                      <> · Último uso: {formatDateTime(s.last_used_at)}</>
                    )}
                  </p>
                  {s.ip && (
                    <p className="text-xs font-mono text-muted-foreground">
                      IP: {s.ip}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevokeSession(s.jti)}
                  disabled={revokingSessionJti === s.jti}
                  className="text-red-600 hover:bg-red-50"
                  data-testid={`revoke-session-${s.jti}`}
                >
                  {revokingSessionJti === s.jti ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Encerrar"
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ===== Revoke consent ===== */}
      <Card className="p-6 space-y-3" data-testid="revoke-card">
        <div>
          <h2 className="text-lg font-medium">Revogar consentimento</h2>
          <p className="text-sm text-muted-foreground mt-1">
            LGPD Art. 8º §5º — Você pode retirar o consentimento a qualquer
            momento. Ao confirmar, sua conta será <strong>suspensa</strong>{" "}
            (você não consegue fazer login) e todas as sessões serão encerradas.
            Os dados ficam guardados até você solicitar exclusão.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="revoke-consent-btn"
            >
              <AlertTriangle className="mr-2 h-4 w-4" /> Revogar consentimento
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="revoke-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Revogar consentimento?</AlertDialogTitle>
              <AlertDialogDescription>
                Sua conta será suspensa e você não conseguirá fazer login até
                aceitar novamente os Termos de Uso. Esta ação NÃO exclui seus
                dados — para isso, use o botão "Excluir minha conta" abaixo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="revoke-cancel-btn">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRevoke}
                disabled={revoking}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="revoke-confirm-btn"
              >
                {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sim, revogar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>

      {/* ===== Delete account ===== */}
      <Card className="p-6 space-y-3 border-red-200" data-testid="delete-card">
        <div>
          <h2 className="text-lg font-medium text-red-700">
            Excluir minha conta
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            LGPD Art. 18 VI — Direito à eliminação. Esta ação é{" "}
            <strong>irreversível</strong>. Seus dados pessoais (nome, e-mail,
            telefone, CPF) serão anonimizados. Registros históricos vinculados
            ao seu identificador (avaliações, negócios) permanecem, mas sem
            qualquer informação pessoal.
          </p>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" data-testid="delete-account-btn">
              <Trash2 className="mr-2 h-4 w-4" /> Excluir minha conta
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir conta permanentemente?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <span>
                  Para confirmar, digite seu e-mail{" "}
                  <strong className="text-foreground">{user.email}</strong>.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-3">
              <Input
                placeholder={user.email}
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                data-testid="delete-confirm-email-input"
              />
              <Textarea
                placeholder="Motivo (opcional)"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={2}
                data-testid="delete-reason-input"
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={
                  deleting ||
                  deleteConfirmEmail.trim().toLowerCase() !==
                    user.email.toLowerCase()
                }
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-confirm-btn"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Excluir definitivamente"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}
